import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { compaBand } from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { money, num } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';

interface GradeRow {
  id: string;
  code: string;
  name: string;
  level: number;
  minSalary: number | null;
  midSalary: number | null;
  maxSalary: number | null;
  notes: string | null;
  isActive: boolean;
  positionCount: number;
  employeeCount: number;
  avgSalary: number | null;
  compaRatio: number | null;
}

/**
 * سلّم الدرجات والرواتب.
 *
 * السلّمُ هو ما يمنع أن يتقاضى اثنان في الوظيفة نفسِها ضعفَ بعضهما:
 * بلا نطاقٍ مكتوبٍ يُحدَّد كلُّ راتبٍ بالتفاوض وحدَه، فيصير الفرقُ بين
 * زميلين أثرًا لجرأةِ أحدهما في المساومة لا لفرقٍ في العمل.
 *
 * ولذلك تُرسم النطاقاتُ على مسطرةٍ واحدةٍ لا تُكتب أرقامًا في جدول:
 * التداخلُ بين درجتين متجاورتين طبيعيٌّ ولا يُقلق — والفجوةُ بينهما هي
 * العطب، ومبلغٌ لا تغطّيه درجةٌ لا يُرى في عمودٍ من الأرقام إلّا بالطرح.
 */
export function Grades() {
  const { atLeast } = useAuth();
  const canEdit = atLeast('ADMIN');
  const [editing, setEditing] = useState<GradeRow | 'new' | null>(null);

  const list = useQuery({
    queryKey: ['hr', 'org', 'grades'],
    queryFn: () => api<GradeRow[]>('/hr/org/grades'),
  });

  /* الدرجةُ الأولى أوّلُ درجٍ في السلّم — فالترتيبُ بالمستوى تصاعديًّا. */
  const rows = (list.data ?? []).slice().sort((a, b) => a.level - b.level);

  /*
   * مسطرةٌ واحدةٌ لكلّ الأشرطة، وإلّا لم يقل الرسمُ شيئًا: شريطٌ يملأ
   * عرضَه في كلّ صفٍّ يجعل أصغرَ درجةٍ كأكبرها. ومتوسّطُ الرواتب داخلٌ
   * في المدى لا مقصوصٌ عليه — المتوسّطُ الخارجُ عن نطاقه هو الخبر.
   */
  const points = rows.flatMap((g) =>
    [g.minSalary, g.maxSalary, g.avgSalary].filter(
      (v): v is number => v !== null,
    ),
  );
  const lo = points.length > 0 ? Math.min(...points) : 0;
  const hi = points.length > 0 ? Math.max(...points) : 0;
  const span = hi - lo;
  const at = (v: number) => (span > 0 ? ((v - lo) / span) * 100 : 0);

  /*
   * الفجوةُ تُقاس بين المفعَّلتين المتجاورتين فقط: الدرجةُ الموقوفةُ لا
   * تغطّي مبلغًا، فلا تُتّهم الشركةُ بفجوةٍ سدَّتها درجةٌ لا تُستعمل،
   * ولا تُخفى فجوةٌ لأنّ بينهما درجةً متوقّفة.
   */
  const ladder = rows.map((g, i) => {
    const next = rows
      .slice(i + 1)
      .find((x) => x.isActive && x.minSalary !== null);
    const gapAfter =
      g.isActive &&
      g.maxSalary !== null &&
      next &&
      next.minSalary !== null &&
      next.minSalary > g.maxSalary
        ? { from: g.maxSalary, to: next.minSalary, next: next.name }
        : null;
    return { g, gapAfter };
  });

  const gaps = ladder.filter((x) => x.gapAfter !== null).length;
  const active = rows.filter((g) => g.isActive).length;
  const noMid = rows.filter((g) => g.isActive && g.midSalary === null).length;
  const positions = rows.reduce((s, g) => s + g.positionCount, 0);
  const employees = rows.reduce((s, g) => s + g.employeeCount, 0);

  return (
    <div className="page">
      <PageHead
        title="الدرجات والرواتب"
        subtitle="سلّمٌ واحدٌ يُقاس عليه كلُّ راتبٍ يُعرَض"
        actions={
          canEdit && (
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              <Icon name="plus" size={15} />
              درجة جديدة
            </button>
          )
        }
      />

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      {list.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {rows.length > 0 && (
        <div className="stats">
          <Stat
            label="الدرجات"
            value={num(rows.length)}
            note={active < rows.length ? `${num(active)} مفعَّلة` : undefined}
          />
          <Stat label="وظائف على السلّم" value={num(positions)} />
          <Stat label="موظّفون في درجات" value={num(employees)} />
          <Stat
            label="فجوات في السلّم"
            value={num(gaps)}
            note={gaps > 0 ? 'مبلغٌ لا تغطّيه درجة' : 'السلّم متّصل'}
          />
        </div>
      )}

      {/*
        الحدُّ يُكتب في الشاشة: من يرى «—» مكانَ النسبة يظنّ الدرجةَ سليمةً
        وإنّما هي بلا مسطرة. والسببُ الذي من أجله يُكتب السلّمُ أصلًا يُقال
        هنا لا في دليلٍ لا يُقرأ.
      */}
      <Notice title="بلا وسطٍ لا نسبة" icon="info">
        النسبةُ إلى الوسط تُقسم على وسط الدرجة، فدرجةٌ بلا وسطٍ مضبوطٍ تظهر
        بلا نسبة — وليس معنى ذلك أنّ رواتبها سليمة، بل أنّه لا مسطرةَ تقيسها.
        {noMid > 0 && <> وعندك {num(noMid)} درجةً مفعَّلةً بلا وسط.</>}
        والسُّلّمُ نفسُه هو ما يمنع أن يتقاضى اثنان في الوظيفة نفسِها ضعفَ
        بعضهما: النطاقُ يُكتب مرّةً، ثمّ يُقاس عليه كلُّ راتبٍ يُعرَض بعده.
      </Notice>

      {gaps > 0 && (
        <Notice title="فجوةٌ بين درجتين" icon="alert" kind="warning">
          تداخلُ نطاقَي درجتين متجاورتين طبيعيّ — الترقّيةُ لا تقفز. أمّا
          الفجوةُ فمبلغٌ لا تغطّيه أيُّ درجة: من يُعرَض عليه راتبٌ في هذا
          المدى لا يجد درجةً يُوضع فيها، فيُكتب له استثناءٌ خارج السلّم.
          مواضعُها معلَّمةٌ بين الصفوف.
        </Notice>
      )}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا درجات بعد"
          text="بلا سلّمٍ مكتوبٍ يُحدَّد كلُّ راتبٍ بالتفاوض وحدَه، فيتقاضى اثنان في الوظيفة نفسِها ضعفَ بعضهما ولا أحدَ يعرف لماذا."
          action={
            canEdit ? (
              <button
                className="btn btn-primary"
                onClick={() => setEditing('new')}
              >
                أضف أوّل درجة
              </button>
            ) : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="card-head">
            <h2 className="card-title">السلّم</h2>
            <span className="tiny mute">
              الشريطُ نطاقُ الدرجة على مسطرةٍ واحدةٍ لكلّ الدرجات، والعلامةُ
              الرأسيّةُ وسطُه، والنقطةُ متوسّطُ رواتب من فيه.
            </span>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>الدرجة</th>
                  <th>المستوى</th>
                  <th>النطاق</th>
                  <th>الوسط</th>
                  <th>وظائف</th>
                  <th>موظّفون</th>
                  <th>متوسّط الراتب</th>
                  <th>النسبة إلى الوسط</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {ladder.map(({ g, gapAfter }) => {
                  const band = compaBand(g.compaRatio);
                  const low = g.minSalary;
                  const high = g.maxSalary;
                  const drawable = span > 0 && low !== null && high !== null;
                  const start = low !== null ? at(low) : 0;
                  /* عرضٌ أدنى: درجةٌ حدّاها متساويان شريطُها صفرٌ فيختفي. */
                  const width =
                    high !== null ? Math.max(at(high) - start, 1) : 0;

                  return (
                    <Fragment key={g.id}>
                      <tr>
                        <td>
                          <span className="cell-strong">{g.name}</span>
                          <div className="tiny mute num">{g.code}</div>
                          {g.notes && <div className="tiny mute">{g.notes}</div>}
                          {!g.isActive && (
                            <div className="mt-2">
                              <Badge kind="neutral">موقوفة</Badge>
                            </div>
                          )}
                        </td>
                        <td className="num">{num(g.level)}</td>
                        <td style={{ minWidth: 210 }}>
                          {drawable ? (
                            <>
                              <div className="od-range-track">
                                <span
                                  className="od-range-span"
                                  style={{
                                    insetInlineStart: `${start}%`,
                                    width: `${width}%`,
                                  }}
                                />
                                {g.midSalary !== null && (
                                  <span
                                    className="od-range-mid"
                                    style={{
                                      insetInlineStart: `${at(g.midSalary)}%`,
                                    }}
                                  />
                                )}
                                {g.avgSalary !== null && (
                                  <span
                                    className="od-range-avg"
                                    style={{
                                      insetInlineStart: `${at(g.avgSalary)}%`,
                                    }}
                                  />
                                )}
                              </div>
                              <div
                                className="row tiny mute"
                                style={{ justifyContent: 'space-between' }}
                              >
                                <span className="num">
                                  {money(g.minSalary)}
                                </span>
                                <span className="num">
                                  {money(g.maxSalary)}
                                </span>
                              </div>
                            </>
                          ) : (
                            <span className="tiny mute">
                              {g.minSalary === null && g.maxSalary === null
                                ? 'لا نطاق مكتوب'
                                : 'النطاق ناقص طرفًا'}
                            </span>
                          )}
                        </td>
                        <td className="num">
                          {g.midSalary === null ? '—' : money(g.midSalary)}
                        </td>
                        <td className="num">{num(g.positionCount)}</td>
                        <td className="num">{num(g.employeeCount)}</td>
                        <td className="num">
                          {g.avgSalary === null ? '—' : money(g.avgSalary)}
                        </td>
                        <td>
                          {g.compaRatio === null || band === null ? (
                            <span className="tiny mute">
                              {g.employeeCount === 0
                                ? 'لا أحد فيها'
                                : 'لا وسط لها'}
                            </span>
                          ) : (
                            <span className="row" style={{ gap: 6 }}>
                              <span className="num">{num(g.compaRatio)}%</span>
                              <Badge kind={band.kind}>{band.label}</Badge>
                            </span>
                          )}
                        </td>
                        <td>
                          {canEdit && (
                            <button
                              className="btn btn-sm"
                              onClick={() => setEditing(g)}
                            >
                              تعديل
                            </button>
                          )}
                        </td>
                      </tr>

                      {/*
                        الفجوةُ صفٌّ لا حاشية: من يقرأ السلّم سطرًا سطرًا
                        يمرّ عليها في موضعها، ويعرف بين أيّ درجتين وقعت
                        وكم المبلغُ الذي لا تغطّيه درجة.
                      */}
                      {gapAfter && (
                        <tr>
                          <td colSpan={9}>
                            <span
                              className="row tiny"
                              style={{ color: 'var(--danger)' }}
                            >
                              <Icon name="alert" size={14} />
                              {/*
                                الجملةُ في عنصرٍ واحد: `row` شبكةٌ مرنة،
                                ولو تُركت كلماتُها أبناءً لها لفُرّق بينها
                                بفجوةٍ عند كلّ رقم.
                              */}
                              <span className="grow">
                                فجوةٌ بين «{g.name}» و«{gapAfter.next}»: من{' '}
                                <span className="num">
                                  {money(gapAfter.from)}
                                </span>{' '}
                                إلى{' '}
                                <span className="num">
                                  {money(gapAfter.to)}
                                </span>{' '}
                                لا تغطّيه درجة.
                              </span>
                            </span>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <p className="tiny mute mt-2">
          المتوسّطُ يُحسب من رواتب من هم على رأس العمل في الدرجة فقط —
          والدرجةُ الفارغةُ لا متوسّطَ لها ولا نسبة. والسلّمُ لا يُطبَّق على
          راتبٍ بأثرٍ رجعيّ: تعديلُ النطاق يغيّر ما يُقاس به، لا ما قُبض.
        </p>
      )}

      {editing && (
        <GradeDialog
          grade={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function GradeDialog({
  grade,
  onClose,
}: {
  grade: GradeRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [code, setCode] = useState(grade?.code ?? '');
  const [name, setName] = useState(grade?.name ?? '');
  const [level, setLevel] = useState(grade ? String(grade.level) : '');
  const [minS, setMinS] = useState(
    grade?.minSalary != null ? String(grade.minSalary) : '',
  );
  const [midS, setMidS] = useState(
    grade?.midSalary != null ? String(grade.midSalary) : '',
  );
  const [maxS, setMaxS] = useState(
    grade?.maxSalary != null ? String(grade.maxSalary) : '',
  );
  const [notes, setNotes] = useState(grade?.notes ?? '');
  const [isActive, setIsActive] = useState(grade?.isActive ?? true);

  /* الحقلُ الفارغ «غيرُ مضبوط» لا صفر: الصفرُ نطاقٌ يبدأ من لا شيء. */
  const toNum = (v: string) => (v.trim() === '' ? null : Number(v));
  const minV = toNum(minS);
  const midV = toNum(midS);
  const maxV = toNum(maxS);

  const numbersOk = [minV, midV, maxV].every(
    (v) => v === null || (Number.isFinite(v) && v >= 0),
  );

  /*
   * الترتيبُ يُفحص هنا كما يُفحص في الخادم: الخادمُ يردّ الرسالةَ نفسَها،
   * لكنّ ردَّها بعد الضغط أسوأُ من منعِ الضغط.
   */
  const orderError = !numbersOk
    ? 'المبالغ أرقامٌ موجبة.'
    : minV !== null && maxV !== null && minV > maxV
      ? 'أدنى النطاق أعلى من أقصاه.'
      : midV !== null &&
          ((minV !== null && midV < minV) || (maxV !== null && midV > maxV))
        ? 'الوسط خارج النطاق.'
        : null;

  const levelNum = Number(level);
  const valid =
    code.trim() !== '' &&
    name.trim().length >= 2 &&
    Number.isInteger(levelNum) &&
    levelNum >= 1 &&
    levelNum <= 99 &&
    orderError === null;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: code.trim(),
        name: name.trim(),
        level: levelNum,
        minSalary: minV,
        midSalary: midV,
        maxSalary: maxV,
        notes: notes.trim() || null,
        isActive,
      };
      return grade
        ? api(`/hr/org/grades/${grade.id}`, { method: 'PATCH', body })
        : api('/hr/org/grades', { method: 'POST', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast(grade ? 'حُفظت الدرجة' : 'أُضيفت الدرجة');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={grade ? `تعديل ${grade.name}` : 'درجة جديدة'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="gr-name">
            اسم الدرجة
          </label>
          <input
            id="gr-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="أخصائي أول"
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="gr-code">
            الكود
          </label>
          <input
            id="gr-code"
            className="input num"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="G4"
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="gr-level">
            المستوى
          </label>
          <input
            id="gr-level"
            className="input num"
            type="number"
            min={1}
            max={99}
            value={level}
            onChange={(e) => setLevel(e.target.value)}
          />
          <span className="tiny mute">
            ترتيبُ الدرجة في السلّم — واحدٌ أوّلُ درجٍ فيه.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="gr-min">
            أدنى النطاق
          </label>
          <input
            id="gr-min"
            className="input num"
            type="number"
            min={0}
            value={minS}
            onChange={(e) => setMinS(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="gr-mid">
            وسط النطاق
          </label>
          <input
            id="gr-mid"
            className="input num"
            type="number"
            min={0}
            value={midS}
            onChange={(e) => setMidS(e.target.value)}
          />
          {midV === null && (
            <span className="tiny mute">
              بلا وسطٍ لن تُحسب نسبةٌ لأحدٍ في هذه الدرجة — النسبةُ قسمةٌ عليه.
            </span>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="gr-max">
            أقصى النطاق
          </label>
          <input
            id="gr-max"
            className="input num"
            type="number"
            min={0}
            value={maxS}
            onChange={(e) => setMaxS(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            مفعَّلة
          </label>
          <span className="tiny mute">
            الإيقافُ لا الحذف — وظائفُ وموظّفون يشيرون إليها.
          </span>
        </div>

        {orderError && (
          <div className="field span-2">
            <span className="tiny" style={{ color: 'var(--danger)' }}>
              {orderError}
            </span>
          </div>
        )}

        <div className="field span-2">
          <label className="label" htmlFor="gr-notes">
            ملاحظات
          </label>
          <textarea
            id="gr-notes"
            className="textarea"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="ما الذي يميّز هذه الدرجة عمّا تحتها — تُقرأ في السلّم."
          />
        </div>
      </div>

      <p className="tiny mute mt-4">
        تداخلُ النطاق مع الدرجة التي تليه مقصودٌ لا خطأ: الترقّيةُ انتقالٌ لا
        قفزة. والذي يُراجَع هو الفجوة — مبلغٌ بين درجتين لا تغطّيه واحدةٌ
        منهما، فمن يُعرَض عليه راتبٌ فيه يخرج عن السلّم.
      </p>
    </Modal>
  );
}
